import { useCallback, useEffect, useRef, useState } from 'react';

const CHAVE_NO_NAVEGADOR = 'spark.chatIa.janela';
export const TAMANHO_PADRAO = { largura: 400, altura: 600 };
export const TAMANHO_MINIMO = { largura: 320, altura: 400 };
export const PASSO_DO_TECLADO = 24;
// Mesmas folgas do CSS do Painel: 16px de cada lado e, em cima, o que sobra acima do botão (margem + 80).
const FOLGA_LATERAL = 32;
const FOLGA_VERTICAL = 80;

const limitar = (valor, minimo, maximo) => Math.round(Math.min(Math.max(valor, minimo), Math.max(minimo, maximo)));

const ehTamanhoValido = (valor) => Number.isFinite(valor) && valor > 0;

// localStorage pode lançar (modo privado, bloqueado): sem ele, a janela só não lembra o tamanho.
const lerSalvo = () => {
    try {
        const salvo = JSON.parse(window.localStorage.getItem(CHAVE_NO_NAVEGADOR));
        if (ehTamanhoValido(salvo?.largura) && ehTamanhoValido(salvo?.altura)) {
            return { largura: salvo.largura, altura: salvo.altura, expandida: salvo.expandida === true };
        }
    } catch {
        // ignora: usa o tamanho padrão
    }
    return { ...TAMANHO_PADRAO, expandida: false };
};

const salvar = (estado) => {
    try {
        window.localStorage.setItem(CHAVE_NO_NAVEGADOR, JSON.stringify(estado));
    } catch {
        // ignora: o tamanho vale até recarregar a página
    }
};

/**
 * Tamanho da janela flutuante: largura e altura (arrastando o canto/bordas ou pelas setas do teclado) e o modo
 * expandido. A janela fica presa no canto inferior direito, então arrastar para a esquerda/para cima aumenta.
 * O tamanho vai para o localStorage ao soltar o mouse e a cada mudança pelo teclado ou pelo botão.
 */
export const useTamanhoDaJanela = ({ margemInferior, painelRef }) => {
    const [estado, setEstado] = useState(lerSalvo);
    const [arrastando, setArrastando] = useState(false);
    const margemRef = useRef(margemInferior);
    margemRef.current = margemInferior;

    // Arraste em andamento: soltar() tira os listeners da window (também ao desmontar no meio do arraste).
    const soltarRef = useRef(null);
    useEffect(() => () => soltarRef.current?.(), []);

    // Salva só o que o usuário mudou (não o estado lido ao montar), e no arraste só ao soltar.
    const estadoInicialRef = useRef(estado);
    useEffect(() => {
        if (!arrastando && estado !== estadoInicialRef.current) salvar(estado);
    }, [estado, arrastando]);

    const ajustar = useCallback(
        ({ largura, altura }) => ({
            largura: limitar(largura, TAMANHO_MINIMO.largura, window.innerWidth - FOLGA_LATERAL),
            altura: limitar(altura, TAMANHO_MINIMO.altura, window.innerHeight - margemRef.current - FOLGA_VERTICAL),
        }),
        []
    );

    // Parte do tamanho na tela (expandida, o CSS manda); sem layout (testes), do estado.
    const tamanhoNaTela = useCallback(() => {
        const retangulo = painelRef.current?.getBoundingClientRect();
        return {
            largura: retangulo?.width || estado.largura,
            altura: retangulo?.height || estado.altura,
        };
    }, [painelRef, estado.largura, estado.altura]);

    /** `eixo`: 'ambos' (canto), 'largura' (borda esquerda) ou 'altura' (borda de cima). */
    const iniciarArraste = useCallback(
        (evento, eixo = 'ambos') => {
            if (evento.button !== 0) return;
            evento.preventDefault();
            const inicio = { x: evento.clientX, y: evento.clientY, ...tamanhoNaTela() };

            const mover = (movimento) => {
                const largura = eixo === 'altura' ? inicio.largura : inicio.largura + inicio.x - movimento.clientX;
                const altura = eixo === 'largura' ? inicio.altura : inicio.altura + inicio.y - movimento.clientY;
                setEstado({ ...ajustar({ largura, altura }), expandida: false });
            };
            const soltar = () => {
                window.removeEventListener('mousemove', mover);
                window.removeEventListener('mouseup', soltar);
                document.body.style.userSelect = selecaoAnterior;
                soltarRef.current = null;
                setArrastando(false);
            };

            // Sem isso o arraste seleciona o texto da página por baixo.
            const selecaoAnterior = document.body.style.userSelect;
            document.body.style.userSelect = 'none';
            soltarRef.current?.();
            soltarRef.current = soltar;
            setArrastando(true);
            window.addEventListener('mousemove', mover);
            window.addEventListener('mouseup', soltar);
        },
        [ajustar, tamanhoNaTela]
    );

    const redimensionarPeloTeclado = useCallback(
        (evento) => {
            const variacao = {
                ArrowLeft: [PASSO_DO_TECLADO, 0],
                ArrowRight: [-PASSO_DO_TECLADO, 0],
                ArrowUp: [0, PASSO_DO_TECLADO],
                ArrowDown: [0, -PASSO_DO_TECLADO],
            }[evento.key];
            if (!variacao) return;
            evento.preventDefault();
            const atual = tamanhoNaTela();
            setEstado({
                ...ajustar({ largura: atual.largura + variacao[0], altura: atual.altura + variacao[1] }),
                expandida: false,
            });
        },
        [ajustar, tamanhoNaTela]
    );

    const restaurarPadrao = useCallback(() => setEstado({ ...TAMANHO_PADRAO, expandida: false }), []);
    const alternarExpansao = useCallback(() => setEstado((atual) => ({ ...atual, expandida: !atual.expandida })), []);

    return {
        largura: estado.largura,
        altura: estado.altura,
        expandida: estado.expandida,
        arrastando,
        iniciarArraste,
        redimensionarPeloTeclado,
        restaurarPadrao,
        alternarExpansao,
    };
};
